import * as React from 'react'
import { useChartDisc } from '@/lib/api/company/chartScan'

import TachoDiscHaeder from './components/TachoDiscHaeder'
import TachoDiscContent from './components/TachoDiscContent'

type Props = {
  companyId: string
  discId: number | null
  isOpen?: boolean
  onClose?: () => void
  onSaved?: () => void
  // Load every disc of the parent scan, not just `discId`, so the header's
  // prev/next can page through them. The import flow needs this — one scanned
  // sheet often holds several discs; "Pokaż tarczkę" opens a single one.
  includeSiblings?: boolean

  positionOffset?: number
  totalDiscCount?: number

  hasMoreScans?: boolean
}

const TachoDisc = ({
  companyId,
  discId,
  onClose,
  onSaved,
  includeSiblings,
  positionOffset = 0,
  totalDiscCount,
  hasMoreScans = false,
}: Props) => {
  const { data: detail, isLoading, isFetchedAfterMount } = useChartDisc(
    companyId,
    discId,
    includeSiblings,
  )
  const discs = detail?.discs ?? []


  const [index, setIndex] = React.useState(0)
  const syncedDiscIdRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    if (!detail) return
    if (syncedDiscIdRef.current === discId) return
    const i = (detail.discs ?? []).findIndex((d) => d?.discId === discId)
    setIndex(i >= 0 ? i : 0)
    syncedDiscIdRef.current = discId ?? null
  }, [detail, discId])

  const disc = discs[index] ?? null

  return (
    <div
      className={`tw-absolute tw-inset-0 tw-z-[209] tw-bg-black/40`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div
        className="tw-absolute tw-bottom-0 tw-left-0 tw-z-[100] tw-flex tw-h-full tw-max-h-[95vh] tw-w-full tw-flex-1 tw-flex-col tw-rounded-t-lg tw-border-t-2 tw-border-base-300 tw-bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <TachoDiscHaeder driverName={''} onClose={onClose} />
        <TachoDiscContent
          key={disc?.discId ?? index}
          companyId={companyId}
          disc={disc}
          // The editor seeds itself once, so a cached disc from before the last save would stick.
          isLoading={isLoading || !isFetchedAfterMount}
          scanId={detail?.scanId ?? -1}
          discIndex={index}
          discCount={discs.length}
          onPrev={index > 0 ? () => setIndex((i) => i - 1) : undefined}
          onNext={
            index < discs.length - 1 ? () => setIndex((i) => i + 1) : undefined
          }
          globalIndex={positionOffset + index}
          globalCount={totalDiscCount ?? discs.length}
          hasMoreAfter={hasMoreScans}
          onSaved={onSaved}
          onClose={onClose}
        />
      </div>
    </div>
  )
}

export default TachoDisc
